import React, { createContext, useContext, useState, useEffect } from "react";
import api from "../lib/api";
import { getAuthToken, isImpersonating, exitImpersonation } from "../lib/impersonation";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // getAuthToken() returns the per-tab impersonation token in a "view as client"
    // tab, else the manager's normal login — so this tab boots as the right identity.
    const token = getAuthToken();
    if (token) {
      fetchUser(token);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async (token) => {
    try {
      const response = await api.get("/auth/me", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      // Only remove token on actual auth failures (401), not network errors
      if (error.response?.status === 401) {
        // In a "view as client" tab, never touch the manager's shared localStorage
        // login — just drop the (expired) impersonation token.
        if (isImpersonating()) {
          exitImpersonation();
        } else {
          localStorage.removeItem("token");
        }
        setUser(null);
      } else {
        // For network errors, try to decode token locally to check if valid
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          if (payload.exp && payload.exp * 1000 > Date.now()) {
            // Token not expired, might be network issue - retry once
            console.log("Network error fetching user, token still valid");
          } else {
            // Token expired
            localStorage.removeItem("token");
            setUser(null);
          }
        } catch {
          localStorage.removeItem("token");
          setUser(null);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await api.post("/auth/login", { email, password });
    const { token, user: userData } = response.data;
    localStorage.setItem("token", token);
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password, role) => {
    const response = await api.post("/auth/register", { name, email, password, role });
    const { token, user: userData } = response.data;
    localStorage.setItem("token", token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    // In a "view as client" tab, "logout" just exits the preview — don't clear the
    // manager's real login (shared across their other tabs).
    if (isImpersonating()) {
      exitImpersonation();
    } else {
      localStorage.removeItem("token");
    }
    setUser(null);
  };

  const refreshUser = async () => {
    const token = getAuthToken();
    if (token) {
      await fetchUser(token);
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateUser, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};
